import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  Image,
  ImageSourcePropType,
  Keyboard,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import ServiceRow from '../components/home/ServiceRow';
import Icon from '../components/ui/Icon';
import Text from '../components/ui/Text';
import {
  Category,
  popularServices,
  RATINGS_ARE_INVENTED,
  sectionsInSub,
  Service,
  servicesInSub,
  SubCategory,
} from '../data/services';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/** The banner's width over its height. Wide enough to read as a header. */
const HERO_ASPECT = 2.1;

/** Height of the bar that holds back and search. */
const BAR_HEIGHT = 56;

/** How long each picture is held before the hero advances on its own. */
const DWELL_MS = 4500;

/**
 * Time to leave the rail alone after a manual swipe. Resuming the moment a
 * drag ends yanks the picture out from under someone still looking at it.
 */
const RESUME_MS = 7000;

type Props = {
  sub: SubCategory;
  category: Category;
  /** Service key → how many are in the cart. */
  cart: Record<string, number>;
  onChangeQuantity: (service: Service, delta: number) => void;
  onBack: () => void;
  onOpenService: (service: Service) => void;
  /**
   * The services to list, when the caller already knows them.
   *
   * Passed by Home whenever the catalogue came from the platform. The seed's
   * subcategories are a grouping that exists only in this app - the server has
   * categories and nothing finer - so `servicesInSub` finds nothing for a
   * server slug and the page would render an empty shelf under a real
   * category. Given a list, it shows that instead and skips the sibling rail,
   * which has no meaning without subcategories.
   */
  services?: Service[];
};

/**
 * Everything under one subcategory.
 *
 * Top to bottom: a banner carrying the bar, the name and how it is rated, the
 * sibling subcategories, then the services themselves as rows.
 *
 * The siblings are here so this page is not a dead end. Someone who opened
 * "Kitchen & bathroom" and wants "Home cleaning" would otherwise have to go
 * back to Home and reopen the sheet - two taps to move one shelf across.
 *
 * The bar floats over the banner and gains its own background as the banner
 * scrolls away, so back is reachable at any scroll position without costing a
 * permanent strip of the screen.
 */
function SubCategoryScreen({
  sub,
  category,
  cart,
  onChangeQuantity,
  onBack,
  onOpenService,
  services: provided,
}: Props) {
  const resp = useResponsive();
  /*
   * The hero pages by the width of the page it is in, not of the window. On a
   * tablet this screen is a centred column, and a pager striding by the full
   * window width would leave every picture part-way off its own frame.
   */
  const width = resp.contentWidth;

  const items = useMemo(
    () => provided ?? servicesInSub(sub),
    [provided, sub],
  );
  /* Headed runs. A subcategory with no sections comes back as a single run
     named after itself, so there is one shape to render. A provided list has
     no sections at all - the server does not group below a category - so it
     renders as one run named after the category. */
  const sections = useMemo(
    () =>
      provided
        ? [{ title: sub.label, services: provided }]
        : sectionsInSub(sub),
    [provided, sub],
  );

  /** Keys of the services worth a label, resolved once. */
  const popular = useMemo(
    () => new Set(popularServices().map(service => service.key)),
    [],
  );

  /*
   * Rated by its own services rather than by a number of its own.
   *
   * The average is weighted by review count, so a service with four million
   * reviews is not outvoted by one with four hundred. `reviews` is display
   * text ("7.7M", "410K"), so it has to be parsed back to a number first.
   */
  const summary = useMemo(() => {
    const count = (text: string): number => {
      const value = parseFloat(text);
      if (Number.isNaN(value)) {
        return 0;
      }
      if (text.includes('M')) {
        return value * 1e6;
      }
      if (text.includes('K')) {
        return value * 1e3;
      }
      return value;
    };

    let weight = 0;
    let total = 0;
    items.forEach(service => {
      /* Unrated services are left out of the average rather than counted as
         zero - see `Service.rating`. A shelf of brand-new services therefore
         has no rating at all, which is the honest answer. */
      if (service.rating === undefined || service.reviews === undefined) {
        return;
      }
      const n = count(service.reviews);
      weight += n;
      total += n * service.rating;
    });

    const reviews =
      weight >= 1e6
        ? `${(weight / 1e6).toFixed(1)}M`
        : weight >= 1e3
        ? `${Math.round(weight / 1e3)}K`
        : String(Math.round(weight));

    return { rating: weight ? total / weight : 0, reviews };
  }, [items]);

  /*
   * Search, scoped to this page.
   *
   * Deliberately not the app-wide search: someone who has drilled into
   * "Kitchen & bathroom" and types "deep" is narrowing what is in front of
   * them, and throwing them back out to every match in the catalogue would
   * undo the two taps they just made. The empty state says so, because a
   * search that finds nothing here while the word exists elsewhere in the app
   * would otherwise look broken.
   */
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return sections;
    }
    return sections
      .map(section => ({
        title: section.title,
        services: section.services.filter(service =>
          `${service.name} ${service.blurb}`.toLowerCase().includes(needle),
        ),
      }))
      .filter(section => section.services.length > 0);
  }, [query, sections]);

  /*
   * Shaped by the artwork's ratio, then held to a share of the screen.
   *
   * The ratio alone is right on a phone in portrait and wrong everywhere else:
   * in landscape, or on a short phone, `width / 2.1` is most of the visible
   * page and the list the screen exists for starts below the fold. A third of
   * the height is as much as a header picture may take before it stops being
   * a header.
   */
  const heroHeight = Math.min(
    Math.round(width / HERO_ASPECT),
    Math.round(resp.height * 0.34),
  );

  /*
   * Every photograph under this subcategory, not just the first.
   *
   * Kitchen & bathroom holds two services with two different pictures, and
   * showing only the kitchen one said the page was about kitchens. Duplicates
   * are dropped so a subcategory whose services share artwork does not page
   * through the same picture twice.
   */
  const art = useMemo(() => {
    const seen: ImageSourcePropType[] = [];
    items.forEach(service => {
      if (service.image && !seen.includes(service.image)) {
        seen.push(service.image);
      }
    });
    return seen;
  }, [items]);

  /* ------------------------------------------------------- the hero's rail */
  const heroRef = useRef<ScrollView>(null);
  const [slide, setSlide] = useState(0);
  /** Mirrors `slide` for the timer, which must not be rebuilt on each tick. */
  const slideRef = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const arm = useCallback(
    (delay: number) => {
      stop();
      if (art.length < 2) {
        return;
      }
      timer.current = setTimeout(() => {
        const next = (slideRef.current + 1) % art.length;
        slideRef.current = next;
        setSlide(next);
        heroRef.current?.scrollTo({ x: next * width, animated: true });
        arm(DWELL_MS);
      }, delay);
    },
    [art.length, stop, width],
  );

  useEffect(() => {
    arm(DWELL_MS);
    return stop;
  }, [arm, stop]);

  const onSettled = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const landed = Math.round(event.nativeEvent.contentOffset.x / width);
      slideRef.current = landed;
      setSlide(landed);
      arm(RESUME_MS);
    },
    [arm, width],
  );

  /*
   * Drives the bar's background. `useNativeDriver` keeps the interpolation off
   * the JS thread, which matters here because it runs on every scroll frame.
   */
  const scrollY = useRef(new Animated.Value(0)).current;
  const barOpacity = scrollY.interpolate({
    inputRange: [heroHeight - BAR_HEIGHT * 2, heroHeight - BAR_HEIGHT],
    outputRange: [0, 1],
    extrapolate: 'clamp',
  });

  const onScroll = useMemo(
    () =>
      Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
        useNativeDriver: true,
      }),
    [scrollY],
  );

  return (
    <View style={styles.root}>
      {/*
        `Animated.ScrollView`, and this is not a style choice.

        `onScroll` below is an `Animated.event` with `useNativeDriver: true`,
        which does NOT return a plain function - it returns a handler the
        native driver binds to an ANIMATED component. Give it to an ordinary
        `ScrollView` and React calls it like a callback on the first scroll
        frame: `TypeError: Object is not a function`, and the app dies.

        It hid for so long because it only fires when a shelf is long enough to
        scroll at all. Every category fitted on one screen, so the handler was
        never called - until a service was moved into one and made it six rows
        long, at which point that one category crashed on touch and every other
        category went on working perfectly. `OnboardingScreen` had this right.
      */}
      <Animated.ScrollView
        showsVerticalScrollIndicator={false}
        /* Zero on a phone; a centred column on a tablet. */
        contentContainerStyle={[styles.content, resp.column]}
        onScroll={onScroll}
        scrollEventThrottle={16}
      >
        {/* ------------------------------------------------------------ hero */}
        <View style={{ height: heroHeight }}>
          {art.length ? (
            <ScrollView
              ref={heroRef}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onScrollBeginDrag={stop}
              onMomentumScrollEnd={onSettled}
              // A slow drag can end without momentum, which leaves the timer off.
              onScrollEndDrag={() => arm(RESUME_MS)}
            >
              {art.map((image, i) => (
                <Image
                  key={i}
                  source={image}
                  style={{ width, height: heroHeight }}
                  resizeMode="cover"
                />
              ))}
            </ScrollView>
          ) : (
            <View style={[styles.heroArt, styles.heroFallback]}>
              <Icon name={category.icon} size={48} color={colors.primary} />
            </View>
          )}

          {art.length > 1 ? (
            <View
              style={styles.dots}
              accessible
              accessibilityRole="progressbar"
              accessibilityLabel={`Picture ${slide + 1} of ${art.length}`}
              pointerEvents="none"
            >
              {art.map((_, i) => (
                <View
                  key={i}
                  style={[styles.dot, i === slide ? styles.dotActive : null]}
                />
              ))}
            </View>
          ) : null}
        </View>

        {/* ----------------------------------------------------------- title */}
        <View style={styles.title}>
          <Text variant="titleLg">{sub.label}</Text>
          {/*
            The last place a fabricated rating was still on screen.

            `RATINGS_ARE_INVENTED` turned the others off - the card, the row,
            and both blocks on the detail sheet - but this one was missed, and
            it was the worst of them. The others at least had a seeded number
            to show; this is an average OF those numbers, and the live
            catalogue shares no service with the seed, so every category
            averaged nothing and printed **"0.00 (0 reviews)"** over its own
            name. A customer reads that as a shelf nobody has ever booked.

            Hidden, not replaced with a line about there being no ratings.
            Nowhere in this app says that any more - see the banner on
            `ServiceScreen`, which used to and no longer does. A sentence about
            an absence is still a sentence about ratings, and under a shelf
            heading it is a disclaimer nobody asked for beneath a title that
            reads perfectly well on its own.

            When ratings become real - `Review` joined to `Booking.serviceId`,
            aggregated per service - flipping the flag brings this back with
            the rest, and `summary` above already computes it correctly.
          */}
          {RATINGS_ARE_INVENTED ? null : (
            <View style={styles.rating}>
              <Icon name="starFilled" size={13} color={colors.rating} />
              <Text variant="caption">{summary.rating.toFixed(2)}</Text>
              <Text variant="caption" color={colors.inkMuted}>
                ({summary.reviews} reviews)
              </Text>
            </View>
          )}
        </View>

        {/* -------------------------------------------------------- services */}
        {query.trim() && !shown.length ? (
          <View style={styles.empty}>
            <Text variant="subtitle" align="center">
              Nothing matches "{query.trim()}"
            </Text>
            <Text variant="bodySm" color={colors.inkMuted} align="center">
              This searches {sub.label} only. Clear it to see everything here.
            </Text>
          </View>
        ) : null}

        {shown.map(section => (
          <View key={section.title} style={styles.list}>
            <Text variant="titleLg" style={styles.listTitle}>
              {section.title}
            </Text>

            {section.services.map((service, i) => (
              <View key={service.key}>
                {i > 0 ? <View style={styles.divider} /> : null}
                <ServiceRow
                  service={service}
                  category={category}
                  quantity={cart[service.key] ?? 0}
                  onChangeQuantity={onChangeQuantity}
                  onOpenDetails={onOpenService}
                  popular={popular.has(service.key)}
                />
              </View>
            ))}
          </View>
        ))}
      </Animated.ScrollView>

      {/*
        Outside the ScrollView, so it stays put. `box-none` is not needed -
        the bar is opaque once the banner has gone and its own height is the
        only thing it covers.
      */}
      <View
        style={[
          styles.bar,
          /* Pinned over the page rather than inside it, so it centres on the
             column by hand. Zero on a phone. */
          { paddingHorizontal: screenPadding + resp.sideInset },
        ]}
      >
        <Animated.View
          style={[styles.barFill, { opacity: barOpacity }]}
          pointerEvents="none"
        />

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={onBack}
          hitSlop={10}
          style={({ pressed }) => [
            styles.chip,
            pressed ? styles.pressed : null,
          ]}
        >
          <Icon name="chevronLeft" size={20} color={colors.ink} />
        </Pressable>

        {searching ? (
          <View style={styles.search}>
            <Icon
              name="search"
              size={16}
              color={colors.inkMuted}
              strokeWidth={1.8}
            />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder={`Search in ${sub.label}`}
              placeholderTextColor={colors.inkSubtle}
              autoFocus
              returnKeyType="search"
              style={styles.searchInput}
              accessibilityLabel={`Search in ${sub.label}`}
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close search"
              hitSlop={10}
              onPress={() => {
                setQuery('');
                setSearching(false);
                Keyboard.dismiss();
              }}
            >
              <Icon
                name="plus"
                size={15}
                color={colors.inkMuted}
                strokeWidth={2.2}
                style={styles.closeGlyph}
              />
            </Pressable>
          </View>
        ) : (
          <>
            {/* Only once the banner's own title has scrolled off, or the two
                read as the same words twice. */}
            <Animated.View style={[styles.barTitle, { opacity: barOpacity }]}>
              <Text variant="subtitle" numberOfLines={1}>
                {sub.label}
              </Text>
            </Animated.View>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Search in ${sub.label}`}
              onPress={() => setSearching(true)}
              hitSlop={10}
              style={({ pressed }) => [
                styles.chip,
                pressed ? styles.pressed : null,
              ]}
            >
              <Icon
                name="search"
                size={18}
                color={colors.ink}
                strokeWidth={1.8}
              />
            </Pressable>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    paddingBottom: spacing.xxl,
  },

  /* Percentages rather than insets: an Image given only insets can fall back
     to its intrinsic size instead of filling. */
  heroArt: {
    width: '100%',
    height: '100%',
  },
  heroFallback: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  /* Over the picture's lower edge rather than under it: below the hero they
     would sit on the title, and the banner is what they belong to. */
  dots: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: spacing.md,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.xs,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    opacity: 0.8,
  },
  dotActive: {
    width: 18,
    opacity: 1,
    backgroundColor: colors.primary,
  },

  title: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    gap: spacing.xs,
  },
  rating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },

  list: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    borderTopWidth: spacing.sm,
    borderTopColor: colors.surfaceMuted,
  },
  /* The weight lives in `titleLg` now - this only sets the space around it. */
  listTitle: {
    paddingTop: spacing.sm,
    paddingBottom: spacing.xs,
  },
  divider: {
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },

  bar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: BAR_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
  },
  barFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.surface,
    ...hairline,
  },
  barTitle: {
    flex: 1,
  },
  /* Opaque on its own, so the field is legible while it is still over the
     banner and the bar behind it has not faded in yet. */
  search: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    height: 38,
    paddingHorizontal: spacing.md,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  searchInput: {
    flex: 1,
    ...inputText,
  },
  // A rotated plus - the icon set has no cross glyph.
  closeGlyph: {
    transform: [{ rotate: '45deg' }],
  },
  empty: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.xxl,
  },
  /* Round and light, so the glyphs read against a photograph as well as
     against the bar's own background once it appears. */
  chip: {
    width: 34,
    height: 34,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default SubCategoryScreen;
